/**
 * The account panel (D176): who the player is and the doors in, as a
 * block of content with no frame of its own — the body of the account
 * dialog, and the whole of the handoff page.
 *
 * @remarks
 * Pure and prop-driven: the panel owns the email the player is typing and
 * nothing about the account. Every door is a callback the shell wires to
 * the identity client, and every outcome comes back as a `notice` the
 * shell sets — the panel never learns whether a magic link was sent, only
 * what to say. platform.md §5's order is the order on the page: the
 * passkey first, the email link as the fallback, Google and Apple for the
 * impatient. No password field exists to draw.
 *
 * Two frames draw it. In the session row it sits inside a modal
 * `<dialog>` behind one button ({@link AccountDialogComponent}); on the
 * handoff page (D170) signing in IS the page, so the same panel is drawn
 * inline under the page's own heading — no trigger behind it, no Close,
 * no second heading saying "Sign in" under the one that already does
 * (Calder, read on the deployed page, 2026-09-12: six sign-in phrases in
 * one viewport). The class names are the dialog's, so the two frames
 * cannot drift in type.
 *
 * @packageDocumentation
 */

import { type DoorsAnswer, type SocialProvider } from '@orpheus/api-contracts';
import { type FormEvent, useId, useState } from 'react';

import { DevicesPanelComponent, type DevicesPanelView } from './devices-panel-component.js';

/** The signed-in account, as the panel shows it. */
export interface AccountView {
	readonly email: string;
}

/**
 * A line for the player after a door was tried (D172): what happened, and
 * whether it is the thing they asked for or a refusal of it. The kind is
 * the panel's to color and a screen reader's to announce; the words are
 * the shell's. Two kinds and not a flag, so a third (a warning, say) is a
 * union member and not a second boolean.
 */
export interface AccountNotice {
	/** `refused`: the door did not open — said in the danger color. `said`: it did, and this is what to know. */
	readonly kind: 'refused' | 'said';
	readonly line: string;
}

/**
 * The account's pass, as the panel shows it (D166): one line of fact and,
 * when there is something to do about it, one button — "Manage" for a
 * holder (Stripe's portal: cancel, resume, the card), "Get the pass" for
 * an account without one. Every word is the shell's.
 */
export interface PassPanelView {
	/** What pressing the button does, and what it says; absent, the line stands alone. */
	readonly action?: { readonly label: string; readonly onPress: () => void };
	/** The button waits while the store answers. */
	readonly isBusy: boolean;
	readonly line: string;
}

/**
 * D170: signing in from a creator's own domain — one door, elsewhere. The
 * account's cookie is for the home site, so the panel offers a trip
 * there and back instead of the doors it cannot open here. Every word is
 * the shell's.
 */
export interface SignInElsewhereView {
	/** The one button's label. */
	readonly label: string;
	/** The lead above it: where the account lives, and that the player comes straight back. */
	readonly lead: string;
	readonly onGo: () => void;
}

/** Props for {@link AccountPanelComponent}. */
export interface AccountPanelComponentProps {
	/** The account signed in, or nothing. */
	readonly account: AccountView | undefined;
	/** D171: where the account is signed in, once the identity has answered; absent, nothing is shown yet. */
	readonly devices?: DevicesPanelView | undefined;
	/**
	 * D173: the doors this identity offers, once it has answered. Absent
	 * (not yet asked, or unreachable), the two doors every deployment has —
	 * the passkey and the email link — are drawn and no provider is: a
	 * button for a door that is not there is worse than a missing button.
	 */
	readonly doors?: DoorsAnswer | undefined;
	/**
	 * D170: on a creator's domain. Signed out, the one door instead of the
	 * three; signed in, no passkey to add — a passkey is bound to the home
	 * site's relying party and a ceremony here can never succeed, and a
	 * button that can never succeed is a broken promise in the chrome
	 * (Calder's ruling, 2026-09-11: absence here, presence on the home site).
	 */
	readonly elsewhere?: SignInElsewhereView | undefined;
	/** A line for the player after a door was tried: "check your email", or a refusal. */
	readonly notice: AccountNotice | undefined;
	/** Signed in: register a passkey on this device. */
	readonly onAddPasskey: () => void;
	/** Signed in, on a build with a store (D166): the account's pass. Absent, nothing about the pass is shown. */
	readonly pass?: PassPanelView | undefined;
	/** Signed out: send the magic link to this address. */
	readonly onSendMagicLink: (email: string) => void;
	/** Signed in: end the session on this device. */
	readonly onSignOut: () => void;
	/** Signed out: sign in with a passkey already registered. */
	readonly onSignInWithPasskey: () => void;
	/** Signed out: leave for the provider's page. */
	readonly onSignInWithSocial: (provider: SocialProvider) => void;
}

/**
 * Render the account panel: the doors, or the account, and the last door's word.
 *
 * @param props - {@link AccountPanelComponentProps}
 * @returns The panel's content, frameless
 */
export const AccountPanelComponent = ({
	account,
	devices,
	doors,
	elsewhere,
	notice,
	onAddPasskey,
	onSendMagicLink,
	onSignInWithPasskey,
	onSignInWithSocial,
	onSignOut,
	pass,
}: AccountPanelComponentProps) => {
	const [email, setEmail] = useState('');
	const emailId = useId();

	const submitMagicLink = (event: FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		onSendMagicLink(email.trim());
	};

	const hasMagicLink = doors?.magicLink !== false;
	const social = doors?.social ?? [];

	return (
		<>
			{account === undefined && elsewhere !== undefined ? (
				<>
					<p className="account-dialog__lead">{elsewhere.lead}</p>
					<button className="account-dialog__door account-dialog__door--primary" onClick={elsewhere.onGo} type="button">
						{elsewhere.label}
					</button>
				</>
			) : undefined}
			{account === undefined && elsewhere === undefined ? (
				<>
					{/*
					 * D176 (Calder, read in place): "account", not "identity". Read on the handoff page this
					 * sentence lands directly under `awayLead`'s "One account covers every game on Orpheus",
					 * which the player finished about thirty seconds earlier on the creator's domain — the same
					 * claim, in a different noun, in the one flow where a suspicious reader is checking whether
					 * our story holds together. Said the same way it reads as confirmation; said two ways it
					 * reads as two claims, and doubt is the thing that flow cannot afford.
					 *
					 * It is the right word here anyway, handoff or no handoff. "Account" is the chrome's noun
					 * everywhere else it speaks — the trigger says "Account — signed out", the dialog is headed
					 * Account, the file is `account-copy.ts` — and "identity" is `identity-api`'s word (D157),
					 * our architecture leaking into the one sentence a player reads about what they are getting.
					 */}
					<p className="account-dialog__lead">
						One account across every game. No password — a passkey on this device, or a link to your email.
					</p>
					<button className="account-dialog__door account-dialog__door--primary" onClick={onSignInWithPasskey} type="button">
						Sign in with a passkey
					</button>
					{hasMagicLink ? (
						<form className="account-dialog__form" onSubmit={submitMagicLink}>
							<label className="account-dialog__label" htmlFor={emailId}>
								Or send a sign-in link to
							</label>
							<div className="account-dialog__row">
								<input
									autoComplete="email"
									className="account-dialog__input"
									id={emailId}
									inputMode="email"
									name="email"
									onChange={(event) => {
										setEmail(event.target.value);
									}}
									required
									type="email"
									value={email}
								/>
								<button className="account-dialog__door" type="submit">
									Send link
								</button>
							</div>
						</form>
					) : undefined}
					{social.length === 0 ? undefined : (
						<div className="account-dialog__row">
							{social.map((provider) => (
								<button
									className="account-dialog__door"
									key={provider}
									onClick={() => {
										onSignInWithSocial(provider);
									}}
									type="button"
								>
									{SOCIAL_DOOR_LABELS[provider]}
								</button>
							))}
						</div>
					)}
				</>
			) : undefined}
			{account === undefined ? undefined : (
				<>
					<p className="account-dialog__lead">
						Signed in as <strong>{account.email}</strong>. Your games follow you to any device you sign in from.
					</p>
					<div className="account-dialog__row">
						{elsewhere === undefined ? (
							<button className="account-dialog__door account-dialog__door--primary" onClick={onAddPasskey} type="button">
								Add a passkey to this device
							</button>
						) : undefined}
						<button className="account-dialog__door" onClick={onSignOut} type="button">
							Sign out
						</button>
					</div>
					{/* D171: every device this account is signed in on, and the way to end any of them. */}
					{devices === undefined ? undefined : <DevicesPanelComponent {...devices} />}
					{/* D166: the pass — a fact and, when there is one, the one thing to do about it. Money is Stripe's page, not this one. */}
					{pass === undefined ? undefined : (
						<div className="account-dialog__pass">
							<p className="account-dialog__lead">{pass.line}</p>
							{pass.action === undefined ? undefined : (
								<button className="account-dialog__door" disabled={pass.isBusy} onClick={pass.action.onPress} type="button">
									{pass.action.label}
								</button>
							)}
						</div>
					)}
				</>
			)}
			<NoticeLine notice={notice} />
		</>
	);
};

/** The social doors' labels — each provider's own phrasing, which its brand guidelines ask for. */
const SOCIAL_DOOR_LABELS: Readonly<Record<SocialProvider, string>> = {
	apple: 'Sign in with Apple',
	google: 'Continue with Google',
};

/**
 * The line under the doors: what the last door said. Always rendered, so a
 * screen reader is already listening when the line arrives; a refusal is an
 * `alert` — assertive, because the player is waiting on exactly this answer
 * — and wears the danger color (D172). A plain line is a polite `status`
 * in the success color (D173): the thing they asked for, done.
 *
 * @param props - The notice, or nothing to say yet
 * @param props.notice - The notice, or nothing to say yet
 * @returns The live region
 */
const NoticeLine = ({ notice }: { readonly notice: AccountNotice | undefined }) =>
	notice?.kind === 'refused' ? (
		<p aria-live="assertive" className="account-dialog__notice account-dialog__notice--refused" role="alert">
			{notice.line}
		</p>
	) : (
		<p aria-live="polite" className={notice === undefined ? 'account-dialog__notice' : 'account-dialog__notice account-dialog__notice--said'} role="status">
			{notice?.line}
		</p>
	);
